import { JSX, splitProps, ParentComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import styles from "./Heading.module.css";

/**
 * Heading Variants
 *
 * Semantic levels (h1-h6) map to HTML elements
 * Visual variants control styling independently of semantic meaning
 */
export type HeadingVariant =
  // Page-level headings
  | "pageTitle" // Large page title (H1)
  | "pageTitleContact" // Large page title for Contact with white text (H1)
  | "pageSubline" // Large page subline (H2)
  | "sectionTitle" // Section headings (H2, uppercase)
  | "cardTitle" // Card/component headings (H3)
  | "episodeItemTitle" // Episode list item title (on accent backgrounds)
  | "subtitle" // Smaller headings (H4-H6)
  | "errorTitle" // Error page title (H1, red)
  | "heroOverline" // Host emoji section title
  | "formHeaderTitle" // Contact page title (H1, white)
  | "formHeaderSubtitle" // Contact page subtitle (H2, white)

  // Specialized variants
  | "showTitle" // Show card titles
  | "trackTitle" // Audio player track titles
  | "footerGroupTitle" // Footer/sidebar column titles
  | "emojiTitle" // Host emoji section title
  | "contentHeadline" // Content block headlines (large, white)
  | "contentOverline" // Content block overline (large, white)
  | "hostCardTitle" // Host card names
  | "articleCardTitle" // Article card titles
  | "accordionTitle" // Accordion item titles
  | "slideHeadline" // Slider headlines
  | "slideSubheadline" // Slider subheadlines
  | "heroStationHeadline" // Station hero headline
  | "fabTitle" // FAB component title
  | "SocialHotlineTitle" // Social hotline section headline

  // Badge/label headings
  | "badgeLabel" // Small uppercase labels

  // Default semantic
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6";

/**
 * Props for the Heading component
 */
export interface HeadingProps {
  /**
   * Visual variant controlling the styling
   * @default "h2"
   */
  variant?: HeadingVariant;

  /**
   * Semantic HTML element to render
   * If not specified, will be inferred from variant
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span";

  /**
   * Center align the text
   */
  center?: boolean;

  /**
   * Add custom CSS classes
   */
  class?: string;

  /**
   * classList for conditional classes
   */
  classList?: Record<string, boolean>;

  /**
   * ID for the heading (useful for anchor links)
   */
  id?: string;

  /**
   * Content to render
   */
  children?: JSX.Element;
}

/**
 * Map variants to their default semantic HTML elements
 */
const variantToElementMap: Record<HeadingVariant, "h1" | "h2" | "h3" | "h4" | "h5" | "h6"> = {
  // Page-level
  pageTitle: "h1",
  pageTitleContact: "h1",
  pageSubline: "h2",
  trackTitle: "h2",
  slideHeadline: "h1",
  errorTitle: "h1",
  heroOverline: "h2",
  formHeaderTitle: "h1",
  formHeaderSubtitle: "h2",

  // Section-level
  sectionTitle: "h2",
  showTitle: "h2",
  contentHeadline: "h2",
  contentOverline: "h3",
  heroStationHeadline: "h1",

  // Card-level
  cardTitle: "h3",
  episodeItemTitle: "h3",
  emojiTitle: "h3",
  accordionTitle: "h3",
  hostCardTitle: "h3",
  articleCardTitle: "h3",
  slideSubheadline: "h3",

  // Smaller
  subtitle: "h4",
  footerGroupTitle: "h4",
  badgeLabel: "h5",
  fabTitle: "h4",
  SocialHotlineTitle: "h3",

  // Semantic defaults
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
};

/**
 * Heading Component
 *
 * A flexible heading component that separates semantic HTML structure
 * from visual styling through variants.
 *
 * @example
 * ```tsx
 * <Heading variant="pageTitle">Welcome</Heading>
 * <Heading variant="sectionTitle" as="h2">Latest Shows</Heading>
 * <Heading variant="cardTitle">Show Name</Heading>
 * ```
 */
export const Heading: ParentComponent<HeadingProps> = (props) => {
  const [local, others] = splitProps(props, [
    "variant",
    "as",
    "center",
    "class",
    "classList",
    "children",
  ]);

  const variant = () => local.variant || "h2";
  const element = () => local.as || variantToElementMap[variant()];

  return (
    <Dynamic
      component={element()}
      class={styles.heading}
      classList={{
        [styles.center]: local.center,
        [styles[variant()]]: true,
        [local.class || ""]: !!local.class,
        ...local.classList,
      }}
      {...others}
    >
      {local.children}
    </Dynamic>
  );
};

export default Heading;
